import { WorkQueue } from './WorkQueue'
import { ReplayerProxy } from '../ReplayerProxy'
import { BaseVideoPlayer } from '../players/BaseVideoPlayer'
import { LoggerName, namedLogger } from '../../../../../../utils/logger'

/**
 * The purpose this handler is to encapsulate the complexity around when the replayer has to pause because one of the videos goes into a wait state
 */
/**
 * How long to wait after a buffer-start event before surfacing a buffering indicator.
 * Short blips below this threshold don't fire onBufferingStart / onBufferingEnd.
 */
export const BUFFER_START_DELAY_MS = 100

const logger = namedLogger(LoggerName.BufferingCoordinator)

export class BufferingCoordinator {
  private replayerProxy: ReplayerProxy
  private readonly videos: BaseVideoPlayer[]
  constructor(replayerProxy: ReplayerProxy, videos: BaseVideoPlayer[]) {
    this.replayerProxy = replayerProxy
    this.videos = videos
    this.setupPluginBufferingEvents()
  }

  private afterSeekWorkQueue = new WorkQueue()
  private seeking = false

  /**
   * Must be called before any seek operation on the replayer to ensure that buffering handling works correctly
   */
  public beforeSeek() {
    logger.debug('bufferingCoordinator.beforeSeek')
    this.seeking = true
  }

  /**
   * Must be called after any seek operation on the replayer to ensure that buffering handling works correctly
   */
  public afterSeek() {
    logger.debug('bufferingCoordinator.afterSeek')
    this.seeking = false
    this.afterSeekWorkQueue.completePendingWork()
  }

  private onBufferingStartCb: ((wasPlayingBeforeBufferingStarted: boolean) => void) | null = null
  private onBufferingEndCb: (() => void) | null = null

  /**
   * Registers a callback to be called when buffering starts
   * @param callback
   */
  public onBufferingStart(callback: (wasPlayingBeforeBufferingStarted: boolean) => void) {
    this.onBufferingStartCb = callback
  }

  /**
   * Registers a callback to be called when buffering ends
   * @param callback
   */
  public onBufferingEnd(callback: () => void) {
    this.onBufferingEndCb = callback
  }

  /**
   * When one or more videos is stuck buffering, we need a way to stop the replayer without pausing the videos that are stuck. If we pause the videos
   * that are buffering, they will not emit the canplay event when they finish buffering, we won't know when it's ok to resume the replayer.
   * We can use this function to pause the videos as they finish buffering, then the whole replayer can be resumed.
   */
  public handlePause() {
    logger.debug('bufferingCoordinator.handlePause')
    if (this.temporarilyPauseForBuffering) {
      this.pauseVideosWhileBufferingCompletes()
    } else {
      this.pauseAllVideos()
    }
  }

  private pauseVideosWhileBufferingCompletes() {
    logger.debug('bufferingCoordinator.pauseVideosWhileBufferingCompletes')
    for (const player of this.videos) {
      if (!player.isBuffering()) {
        player.pause()
      } else {
        player.addEventListener(
          'playing',
          () => {
            logger.debug('previously buffering video has resumed on its own and is now playing', player.getVideoIdentifier())
            /*
              If this is the last video that finishes buffering, the replayer will have already been resumed by the time we get here. This is because 
              we've already registered a canplaythrough event on all the videos the video that will resume the player when all of them finish buffering.
              We can solve this race condition by checking to make sure the replayer is still paused before we pause.
             */
            if (!this.replayerProxy.isPlaying()) {
              player.pause()
            }
          },
          { once: true }
        )
      }
    }
  }

  /**
   * When one or more videos is stuck buffering, we need a way to stop the replayer without pausing the videos that are stuck. If we pause the videos
   * that are buffering, they will not emit the canplay event when they finish buffering, we won't know when it's ok to resume the replayer.
   * We can use this function to pause the videos as they finish buffering, then the whole replayer can be resumed.
   */
  private pauseAllVideos() {
    logger.debug('pauseVideoAllVideos')
    for (const player of this.videos) {
      player.pause()
    }
  }

  private bufferStartDelayTimeout: ReturnType<typeof setTimeout> | undefined

  /**
    This controls how pausing works. When this is true and the replayer is paused, then it doesn't actually pause any buffering videos until they
    finish buffering.
  */
  private temporarilyPauseForBuffering = false

  /**
   * This is called every time a video starts buffering. It sets temporarilyPauseForBuffering and pauses the replayer if it is currently playing.
   */
  private videoBufferingStart = () => {
    if (!this.temporarilyPauseForBuffering) {
      const isPlaying = this.replayerProxy.isPlaying()

      if (isPlaying) {
        logger.debug('bufferingCoordinator.videoBufferingStart - pausing replayer')

        // Often the loading is just a blip, so don't show the buffering indicator immediately. Wait 100ms to see if the buffering ends quickly, and cancel the timeout if it does
        this.bufferStartDelayTimeout = setTimeout(() => {
          this.onBufferingStartCb?.(isPlaying)
          this.bufferStartDelayTimeout = undefined
        }, BUFFER_START_DELAY_MS)

        /*
          We need to pause the replayer during buffering without pausing the videos themselves. The issue is that the videos will not emit the
          canplay event if they are paused. The solution is to tell the plugin to restart the playback after buffering ends, so that the plugin knows
          to monitor all the videos as they finish buffering, and keep them in sync with the replayer.
        */
        this.temporarilyPauseForBuffering = true

        // the player will emit play and pause events while processing a seeing event, so if we go into buffering because of seeking,
        // we need to defer the pause command until after all the seek processing is complete or the seek processing might play it again.
        if (this.seeking) {
          this.afterSeekWorkQueue.queueWork(() => {
            this.replayerProxy.pause()
          })
        } else {
          this.replayerProxy.pause()
        }
      } else {
        logger.debug('bufferingCoordinator.videoBufferingStart - was called but replayer already paused')
      }
    } else {
      logger.debug('bufferingCoordinator.videoBufferingStart - was skipped because temporarilyPauseForBuffering is true')
    }
  }

  /**
   * This is called every time a video finishes buffering. It checks to see if all the shown videos are no longer buffering, and if so, it resumes the replayer
   */
  private videoBufferingEnd = () => {
    if (this.temporarilyPauseForBuffering) {
      let allVideosDoneBuffering = true
      for (const video of this.videos!.values()) {
        // if we have any shown videos that are buffering, then we aren't caught up yet
        if (video.isBuffering() && video.isShown()) {
          allVideosDoneBuffering = false
          break
        }
      }
      if (allVideosDoneBuffering) {
        // if the timeout hasn't fired yet, cancel it
        if (this.bufferStartDelayTimeout) {
          logger.debug('bufferingCoordinator.videoBufferingEnd - clearing bufferStartDelayTimeout')
          clearTimeout(this.bufferStartDelayTimeout)
          this.bufferStartDelayTimeout = undefined
        } else {
          logger.debug('bufferingCoordinator.videoBufferingEnd - executing onBufferingEndCb', {
            console: {
              onBufferingEndCb: this.onBufferingEndCb
            }
          })
          this.onBufferingEndCb?.()
        }

        this.temporarilyPauseForBuffering = false

        // the player will emit play and pause events while processing a seeing event, so if we come out of buffering because of seeking,
        // we need to defer the play command until after all the seek processing is complete or the seek processing might pause it again.
        if (this.seeking) {
          this.afterSeekWorkQueue.queueWork(() => {
            logger.debug('bufferingCoordinator.videoBufferingEnd - calling replayerProxy.play() - queued after seek')
            this.replayerProxy.play()
          })
        } else {
          logger.debug('bufferingCoordinator.videoBufferingEnd - calling replayerProxy.play()')
          this.replayerProxy.play()
        }
      } else {
        logger.debug('bufferingCoordinator.videoBufferingEnd - was called but not resuming replayer because some videos are still buffering')
      }
    } else {
      logger.debug('bufferingCoordinator.videoBufferingEnd - was skipped because temporarilyPauseForBuffering is false')
    }
  }

  private setupPluginBufferingEvents() {
    // also listen for each video player's waiting and playing events
    for (const video of this.videos) {
      video.registerBufferingStartListener(this.videoBufferingStart)
      video.registerBufferingEndListener(this.videoBufferingEnd)
    }
  }
}
